/**
 * TriggerZoneComponent - reusable trigger zone system for area-based interactions.
 * Creates invisible collision volumes that detect when actors enter/exit the area.
 */

import * as THREE from 'three';

import { Pawn } from '../../actors/Pawn.js';
import { PhysicsMotionType } from '../../physics/PhysicsTypes.js';
import { EngineClass } from '../../systems/ClassRegistry.js';
import { DefaultCollisionProfile } from '../../systems/collision/CollisionTypes.js';
import { Delegate } from '../../utils/Delegate.js';
import { property } from '../../utils/serialization/decorator.js';
import { CollisionShapeComponent } from '../CollisionShapeComponent.js';

import type { Actor } from '../../actors/Actor.js';
import type { CollisionShapeComponentOptions } from '../CollisionShapeComponent.js';


/** Actor filtering modes for trigger zones */
export enum TriggerFilter {
  /** Trigger for all actors */
  All = 'all',
  /** Trigger only for player pawns */
  PlayerOnly = 'playerOnly',
  /** Trigger only for Pawn actors */
  PawnsOnly = 'pawnsOnly',
  /** Use custom filter function */
  Custom = 'custom'
}

/** Event delegates for trigger zone interactions */
export type TriggerActorDelegate = Delegate<[Actor, TriggerZoneComponent], void>;

/** Configuration options for TriggerZoneComponent */
export interface TriggerZoneComponentOptions extends CollisionShapeComponentOptions {
  /** Actor filtering mode. Default: TriggerFilter.All */
  filter?: TriggerFilter;
  /** Whether to call onActorStay continuously while actors remain in the zone. Default: false */
  enableStayEvents?: boolean;
}

@EngineClass('TriggerZoneComponent')
export class TriggerZoneComponent extends CollisionShapeComponent {
  @property()
  public filter: TriggerFilter = TriggerFilter.All;

  @property()
  public enableStayEvents: boolean = false;

  /** Set of actors currently inside the trigger zone */
  private readonly actorsInZone: Set<Actor> = new Set();

  /** Custom filter function for TriggerFilter.Custom mode */
  private customFilter: ((actor: Actor) => boolean) | null = null;

  // Event delegates
  /** Called when an actor enters the trigger zone */
  public readonly onActorEntered: TriggerActorDelegate = new Delegate<[Actor, TriggerZoneComponent], void>();

  /** Called when an actor exits the trigger zone */
  public readonly onActorExited: TriggerActorDelegate = new Delegate<[Actor, TriggerZoneComponent], void>();

  /** Called each frame while actors remain in the zone (if enableStayEvents is true) */
  public readonly onActorStay: TriggerActorDelegate = new Delegate<[Actor, TriggerZoneComponent], void>();


  public override initialize(options?: TriggerZoneComponentOptions): void {
    // Set default physics options for trigger zones
    const defaultOptions = {
      ...options,
    };
    defaultOptions.physicsOptions ??= {
      enabled: true,
      motionType: PhysicsMotionType.Static,
      generateCollisionEvents: true,
      collisionProfile: DefaultCollisionProfile.Trigger
    };
    defaultOptions.enableDebugVisualization ??= false;
    defaultOptions.geometry ??= new THREE.BoxGeometry(1, 1, 1);
    defaultOptions.scale ??= new THREE.Vector3(2, 2, 2);

    super.initialize(defaultOptions);

    this.filter = options?.filter ?? this.filter;
    this.enableStayEvents = options?.enableStayEvents ?? this.enableStayEvents;

    // Set up collision event handling immediately
    this.setupCollisionEvents();
  }

  public override postLoad(): void {
    super.postLoad();

    // Re-setup collision events after deserialization
    this.setupCollisionEvents();
  }

  public override beginPlay(): void {
    super.beginPlay();

    console.log('[TriggerZoneComponent] beginPlay() started');
    console.log('[TriggerZoneComponent] beginPlay() completed');
  }

  public override endPlay(): void {
    super.endPlay();

    // Clean up actors in zone
    this.actorsInZone.clear();
  }

  public override tickPrePhysics(deltaTime: number): void {
    super.tickPrePhysics(deltaTime);

    // Handle stay events if enabled
    if (this.enableStayEvents && this.actorsInZone.size > 0) {
      for (const actor of this.actorsInZone) {
        this.onActorStay.invoke(actor, this);
      }
    }
  }

  private setupCollisionEvents(): void {
    // Bind overlap events using the inherited collision component's events
    this.onOverlapWith.add((thisComponent, otherComponent, collisionEvent) => {
      const otherActor = otherComponent.getActor();
      if (otherActor && this.shouldTriggerForActor(otherActor)) {
        if (!this.actorsInZone.has(otherActor)) {
          console.log('[TriggerZoneComponent] Actor entered trigger zone:', otherActor.name);
          this.actorsInZone.add(otherActor);
          this.onActorEntered.invoke(otherActor, this);
        }
      }
    });

    this.onStopOverlappingWith.add((thisComponent, otherComponent, collisionEvent) => {
      const otherActor = otherComponent.getActor();
      if (otherActor && this.shouldTriggerForActor(otherActor)) {
        if (this.actorsInZone.has(otherActor)) {
          console.log('[TriggerZoneComponent] Actor exited trigger zone:', otherActor.name);
          this.actorsInZone.delete(otherActor);
          this.onActorExited.invoke(otherActor, this);
        }
      }
    });
  }

  private shouldTriggerForActor(actor: Actor): boolean {
    switch (this.filter) {
      case TriggerFilter.PlayerOnly:
        return this.isPlayerPawn(actor);

      case TriggerFilter.PawnsOnly:
        return actor instanceof Pawn;

      case TriggerFilter.Custom:
        return this.customFilter ? this.customFilter(actor) : false;

      case TriggerFilter.All:
      default:
        return true;
    }
  }

  private isPlayerPawn(actor: Actor): boolean {
    return actor.getPlayerController() !== null;
  }

  public getActorsInZone(): Actor[] {
    return Array.from(this.actorsInZone);
  }

  public isActorInZone(actor: Actor): boolean {
    return this.actorsInZone.has(actor);
  }

  public getActorCount(): number {
    return this.actorsInZone.size;
  }

  public override getConfigurationWarnings(): string[] {
    const warnings = super.getConfigurationWarnings();

    // Check if collision profile is set to Trigger
    // Access from the parent class's options since we inherit from CollisionShapeComponent
    const physicsOptions = (this as any).options?.physicsOptions;
    const collisionProfile = physicsOptions?.collisionProfile;
    if (collisionProfile && collisionProfile !== DefaultCollisionProfile.Trigger) {
      warnings.push(
        `TriggerZoneComponent collision profile should be set to '${DefaultCollisionProfile.Trigger}' for proper trigger behavior. ` +
        `Current profile: '${collisionProfile}'. This may cause unexpected collision responses.`
      );
    }

    return warnings;
  }

  //====================================================================================================
  // Editor Functions
  //====================================================================================================

  public override getEditorClassIcon(): string | null {
    return 'Icon_TriggerZone';
  }

}